import { useCallback, useState, type ReactElement } from 'react'
import { find, toNumber } from 'lodash-es'
import { Box, Button, IconButton, Popover, Tooltip, useTheme } from '@mui/material'
import { EmptyState } from '@ui2/feedback'
import { MaterialSymbol } from '@ui2/icons'
import { useResultsStore } from '@editorStores'
import { RESULTS2_DETAIL_ROW_HIGHLIGHT_BG } from '../constants'
import { useHiddenSupportPosition } from '../hooks/useHiddenSupportPosition'
import {
  fmt,
  formatKn,
  isSelectedSupportHighlightMatch,
  toggleSelectedSupportHighlight,
} from '../utils'
import LoadCaseCombinationFormula from './LoadCaseCombinationFormula'

type FooterRow = {
  supportName: string
  forceTimber: number
  forceTimberLcc?: LoadCaseCombination
  kMod: number
  forceGzt: number | null
  forceGztLcc?: LoadCaseCombination
  forceAnchoring: number | null
  positionRel: number
}

export const FooterTable = ({
  elementId,
  designForces,
  designForcesWithoutKMods,
  designForcesAnchoring,
}: {
  elementId: string
  designForces: DesignSupportForce[]
  designForcesWithoutKMods: DesignSupportForce[]
  designForcesAnchoring?: DesignSupportForce[]
}): ReactElement => {
  const theme = useTheme()
  const highlightedCheck = useResultsStore(state => state.highlightedCheck)
  const setHighlightedCheck = useResultsStore(state => state.setHighlightedCheck)

  // Hide the second support of rips/columns, matching the supports grid.
  const isHiddenSupportPosition = useHiddenSupportPosition(elementId)

  const [detailsRow, setDetailsRow] = useState<FooterRow | null>(null)
  const [detailsAnchorEl, setDetailsAnchorEl] = useState<HTMLElement | null>(null)
  const closeDetails = () => {
    setDetailsRow(null)
    setDetailsAnchorEl(null)
  }

  const handleRowClick = useCallback(
    (relativePosition: number) => () => {
      toggleSelectedSupportHighlight(
        elementId,
        relativePosition,
        highlightedCheck,
        setHighlightedCheck,
      )
    },
    [elementId, highlightedCheck, setHighlightedCheck],
  )
  const rows = designForces
    .filter(force => !isHiddenSupportPosition(force.relative_position))
    .map(force => {
      const withoutKModForce = find(designForcesWithoutKMods, {
        relative_position: force.relative_position,
      })
      const anchoringForce = designForcesAnchoring
        ? find(designForcesAnchoring, { relative_position: force.relative_position })
        : undefined
      // Matches the OLD Results table exactly: the "Holz" column shows the with-k_mod
      // (designForces) value paired with the without-k_mod LCC, and the "GZT" column
      // shows the without-k_mod value paired with the with-k_mod LCC.
      return {
        supportName: force.support_name,
        forceTimber: force.design_force ?? null,
        forceTimberLcc: force?.lcc,
        kMod: force.k_mod,
        forceGzt: withoutKModForce?.design_force ?? null,
        forceGztLcc: withoutKModForce?.lcc,
        forceAnchoring: anchoringForce?.design_force ?? null,
        positionRel: toNumber(force.relative_position),
      }
    })
  return (
    <Box sx={{ pt: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 1.4fr 1fr 1fr 1fr 1fr 60px',
          fontSize: 12,
          color: 'text.secondary',
          pb: 0.5,
        }}
      >
        <Box>AL</Box>
        <Box>
          F<Box component="sub">d</Box> Holz [kN]
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>
            k<Box component="sub">mod</Box>
          </span>
          <Tooltip
            title="Bei Stahlbauteilen wird ein Ersatzquerschnitt mit C24 und NKL1 für die Ermittlung von k_mod und der Design-Auflagerkraft bei Holz verwendet."
            arrow
          >
            <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
              <MaterialSymbol icon="info" size={16} />
            </Box>
          </Tooltip>
        </Box>

        <Box>
          F<Box component="sub">d</Box> GZT [kN]
        </Box>
        <Box>
          F<Box component="sub">d</Box> Anker [kN]
        </Box>
        <Box>Pos. rel.</Box>
        <Box />
      </Box>

      {rows.length === 0 ? (
        <EmptyState sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          Keine Auflagerkräfte auf dem Element vorhanden
        </EmptyState>
      ) : null}

      {rows.map((row, index) => {
        const isRowHighlighted = isSelectedSupportHighlightMatch(
          elementId,
          highlightedCheck,
          row.positionRel,
        )

        return (
          <Box
            key={`${row.supportName}-${row.positionRel}`}
            onClick={handleRowClick(row.positionRel)}
            sx={{
              display: 'grid',
              gridTemplateColumns: '40px 1.4fr 1fr 1fr 1fr 1fr 60px',
              fontSize: 12,
              py: 0.625,
              borderTop: `1px solid ${theme.palette.divider}`,
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: isRowHighlighted ? RESULTS2_DETAIL_ROW_HIGHLIGHT_BG : 'transparent',
            }}
          >
            <Box sx={{ fontFamily: 'Roboto' }}>{index + 1}</Box>
            <Tooltip
              title={
                row.forceTimberLcc?.data?.length ? (
                  <LoadCaseCombinationFormula lcc={row.forceTimberLcc} />
                ) : (
                  ''
                )
              }
              arrow
              placement="top"
              disableInteractive
            >
              <Box sx={{ width: 'fit-content' }}>
                {row.forceTimber === null ? '-' : formatKn(row.forceTimber)}
              </Box>
            </Tooltip>
            <Box>{fmt(row.kMod)}</Box>
            <Tooltip
              title={
                row.forceGztLcc?.data?.length ? (
                  <LoadCaseCombinationFormula lcc={row.forceGztLcc} />
                ) : (
                  ''
                )
              }
              arrow
              placement="top"
              disableInteractive
            >
              <Box sx={{ width: 'fit-content' }}>
                {row.forceGzt != null ? formatKn(row.forceGzt) : '-'}
              </Box>
            </Tooltip>
            <Box>{row.forceAnchoring != null ? formatKn(row.forceAnchoring) : '-'}</Box>
            <Box>{fmt(row.positionRel)}</Box>
            <Box sx={{ display: 'inline-flex', justifyContent: 'center' }}>
              <IconButton
                size="small"
                title="Lastfallkombinationen"
                onClick={e => {
                  e.stopPropagation()
                  setDetailsRow(row)
                  setDetailsAnchorEl(e.currentTarget)
                }}
              >
                <MaterialSymbol icon="info" size={16} />
              </IconButton>
            </Box>
          </Box>
        )
      })}

      <Popover
        open={Boolean(detailsRow && detailsAnchorEl)}
        anchorEl={detailsAnchorEl}
        onClose={closeDetails}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: 'min(420px, calc(100vw - 24px))',
            borderRadius: 1.25,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Box
            sx={{
              fontFamily: 'Roboto',
              fontSize: 12.5,
              fontWeight: 600,
              color: 'text.primary',
              mb: 1,
            }}
          >
            Lastfallkombinationen{detailsRow ? ` – ${detailsRow.supportName}` : ''}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, fontFamily: 'Roboto' }}>
            <Box>
              <Box sx={{ fontSize: 11, color: 'text.secondary', mb: 0.25 }}>
                F<Box component="sub">d</Box> Holz
              </Box>
              <Box sx={{ fontSize: 12, color: 'text.primary' }}>
                <LoadCaseCombinationFormula lcc={detailsRow?.forceTimberLcc} />
              </Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: 11, color: 'text.secondary', mb: 0.25 }}>
                F<Box component="sub">d</Box> GZT
              </Box>
              <Box sx={{ fontSize: 12, color: 'text.primary' }}>
                <LoadCaseCombinationFormula lcc={detailsRow?.forceGztLcc} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              size="small"
              onClick={closeDetails}
              sx={{ textTransform: 'none', fontFamily: 'Roboto' }}
            >
              Schließen
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}
